import { DrawerTooltipContainer } from 'src/v2/commonComponents/DrawerTooltipContainer';
import { SettingsSwitchItem } from 'src/v2/components/SettingsSwitchItem';
import {
  useConversationParams,
  useSkillCommand,
  useUserSettings,
} from 'src/hooks';
import { SettingsToggle } from '../SettingsToggle';
import {
  imageStyleOptions,
  researchedMediaSettingOptions,
} from 'src/constants';
import {
  QuickSkillCommand,
  SettingKeys,
  SingleSelectSetting,
  ToggleSetting,
} from 'src/types';
import { Dimensions } from 'src/components/ImageGenSettings/components/Dimensions';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { ImageStyleSelect } from 'src/components/ImageGenSettings/components/ImageStyleSelect';
import { EnvelopeSimple } from '@phosphor-icons/react';
import styles from './SkillSettingsControl.module.scss';
import classNames from 'classnames';
import { useMemo } from 'react';
import { capitalizeEachFirstLetter } from 'src/utils';

type SupportedCommand =
  | QuickSkillCommand.IMAGE
  // Temporarily disabled media settings for Researcher
  // | QuickSkillCommand.RESEARCHER
  | QuickSkillCommand.DEEP_RESEARCHER;

const skillConfig: Record<
  SupportedCommand,
  { title: string; content: JSX.Element }
> = {
  [QuickSkillCommand.IMAGE]: {
    title: 'Preference',
    content: (
      <>
        <Dimensions />
        <TextDivider />
        <ImageStyleSelect />
      </>
    ),
  },

  // [QuickSkillCommand.RESEARCHER]: {
  //   title: 'Other resources',
  //   content: (
  //     <>
  //       <div className={styles.header}>Display other related resources</div>
  //       {researchedMediaSettingOptions.map(
  //         ({ value, label, icon, settingKey }) => (
  //           <SettingsSwitchItem
  //             key={value}
  //             value={value}
  //             label={label}
  //             icon={icon}
  //             settingKey={settingKey}
  //           />
  //         ),
  //       )}
  //     </>
  //   ),
  // },

  [QuickSkillCommand.DEEP_RESEARCHER]: {
    title: 'Notification',
    content: (
      <>
        <div className={styles.header}>
          Send email once Deep&nbsp;Research results are available
        </div>
        <div>
          <SettingsSwitchItem
            value={SettingKeys.DEEP_RESEARCH_EMAIL}
            label="Notify via Email"
            icon={EnvelopeSimple}
            settingKey={SettingKeys.DEEP_RESEARCH_EMAIL}
          />
        </div>
      </>
    ),
  },
};

export const SkillSettingsControl = () => {
  const { activeSkillCommand } = useSkillCommand();
  const { isLandingPage } = useConversationParams();

  const config =
    (activeSkillCommand &&
      activeSkillCommand in skillConfig &&
      skillConfig[activeSkillCommand as SupportedCommand]) ||
    null;

  const getTooltipClassName = () => {
    switch (activeSkillCommand) {
      case QuickSkillCommand.IMAGE:
        return styles.imageTooltip;
      case QuickSkillCommand.RESEARCHER:
        return styles.researcherTooltip;
      case QuickSkillCommand.DEEP_RESEARCHER:
        return styles.deepResearchTooltip;
      default:
        return '';
    }
  };

  const tooltipClassName = getTooltipClassName();

  const { setting: researcherSettings } = useUserSettings<
    Record<string, ToggleSetting>
  >(SettingKeys.RESEARCH);

  const { setting: imageSettings } = useUserSettings<
    Record<string, SingleSelectSetting>
  >(SettingKeys.IMAGE);

  const { setting: deepResearchSetting } = useUserSettings<ToggleSetting>(
    SettingKeys.DEEP_RESEARCH_EMAIL,
  );

  const researcherDescription = useMemo(
    () =>
      researchedMediaSettingOptions
        .map(({ settingKey, label }) =>
          researcherSettings?.[settingKey] &&
          !!researcherSettings[settingKey].value
            ? label
            : null,
        )
        .filter((item) => !!item)
        .join(', ') || 'None',
    [researcherSettings],
  );

  const imageDescription = useMemo(
    () =>
      imageSettings
        ? [
            `${capitalizeEachFirstLetter(imageSettings[SettingKeys.IMAGE_DIMENTIONS].value || '')}`,
            `${imageStyleOptions.find(({ value }) => value === imageSettings[SettingKeys.IMAGE_STYLES].value)?.label || ''}`,
          ]
            .filter((item) => !!item)
            .join(', ')
        : '',
    [imageSettings],
  );

  const deepResearchDescription = useMemo(
    () => (deepResearchSetting?.value ? 'Email' : 'None'),
    [deepResearchSetting],
  );

  const description = useMemo(() => {
    switch (activeSkillCommand) {
      case QuickSkillCommand.RESEARCHER:
        return researcherDescription;

      case QuickSkillCommand.IMAGE:
        return imageDescription;

      case QuickSkillCommand.DEEP_RESEARCHER:
        return deepResearchDescription;

      default:
        return '';
    }
  }, [
    activeSkillCommand,
    researcherDescription,
    imageDescription,
    deepResearchDescription,
  ]);

  if (!config) {
    return null;
  }

  return (
    <DrawerTooltipContainer
      id="fast-research-settings"
      place={isLandingPage ? 'bottom' : 'top'}
      classNameTooltip={classNames(styles.tooltip, tooltipClassName)}
      offset={8}
      ToggleButton={SettingsToggle}
      toggleButtonProps={{
        title: config.title,
        description,
      }}
    >
      <div className={styles.content}>{config.content}</div>
    </DrawerTooltipContainer>
  );
};
